import { RouteProp, useNavigation } from "@react-navigation/native";
import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { useSelector } from "react-redux";
import { ComboBox, SLImage } from "src/components";
import { AnimatedMinusSign } from "src/components/impComponents/AnimatedMinusSign";
import { AnimatedPlusSign } from "src/components/impComponents/AnimatedPlusSign";
import { CheckMark2 } from "src/components/NewCheckBox2";
import { fonts } from "src/fonts";
import { kRoute, tRouteParams } from "src/kRoute";
import { kStyleConst } from "src/kStyle";
import { kColors } from "src/kColors";
import { useFetchProduct } from "src/redux/ProductDetails/actions";
import { formatMoney } from "src/util/gFormatting";
import { tOption, tVariant, tVariantOption } from "tmt_api/src/types/types";
import { addProductToCart } from "../redux/ProductDetails/actions";
import { kFonts } from "src/kFonts";
import { PdArrowLeft, PdArrowRight } from "src/components/Icons";
import { useAppDispatch } from "src/redux";
import { ShopSubHeader } from "src/components/connected";
import { PdInputNumber } from "src/components/forms/input";

type tpProductDetails = (props: { route: RouteProp<tRouteParams, typeof kRoute.P101> }) => React.ReactElement;

const kNilUuid = "00000000-0000-0000-0000-000000000000";

const P101_ProductDetails: tpProductDetails = ({ route }) => {
  const myProductId = route.params?.productId;
  const myBrand = route.params?.brand;
  useFetchProduct(myProductId);
  const rCompanyId = useSelector(({ CompanyReducer }) => (CompanyReducer.type === "brand" ? CompanyReducer.id : ""));
  const rCompanyName = useSelector(({ CompanyReducer }) =>
    CompanyReducer.type === "brand" ? CompanyReducer.name : "",
  );
  const rCurrentUserType = useSelector(({ CompanyReducer }) => CompanyReducer?.type);
  const rProduct = useSelector(({ ProductDetailsReducer }) => ProductDetailsReducer.productData);
  const rSmallScreen = useSelector(({ NavigationReducer }) => NavigationReducer.drawerType === "slide");
  const kNav = useNavigation();

  const _onPressBrandName = useCallback(
    () =>
      kNav.navigate(kRoute.P100, {
        brand: myBrand,
        category: undefined,
      }),
    [kNav, myBrand],
  );

  const _onPressPrimaryCategory = useCallback(
    () =>
      kNav.navigate(kRoute.P100, {
        brand: myBrand,
        category: rProduct.category.split(" - ")[0],
      }),
    [kNav, myBrand, rProduct.category],
  );

  const _onPressSecondaryCategory = useCallback(
    () =>
      kNav.navigate(kRoute.P100, {
        brand: myBrand,
        category: rProduct.category,
      }),
    [kNav, myBrand, rProduct.category],
  );

  // const _onPressBookmark = useCallback(() => {
  //   setBookmarked((myBookmarked) => !myBookmarked);
  // }, []);
  const BrandNameButton = useMemo(
    () => <Text style={fonts.H4_BrandDark}>{rProduct.brandName}</Text>,
    [rProduct.brandName],
  );
  const BrandNameSmallerButton = useMemo(
    () => <Text style={fonts.H3_BrandDark}>{rProduct.brandName}</Text>,
    [rProduct.brandName],
  );
  const MainCategoryButton = useMemo(
    () => <Text style={fonts.H4_BrandDark}>{rProduct.category.split(" - ")[0]}</Text>,
    [rProduct.category],
  );
  const SubCategoryButton = useMemo(
    () => <Text style={fonts.H4_BrandDark}>{rProduct.category.split(" - ")[1]}</Text>,
    [rProduct.category],
  );

  // ensures a brand doesn't see other brand's page
  useEffect(() => {
    if (
      rCurrentUserType === "brand" &&
      !!rCompanyId &&
      !!rProduct.brandUid &&
      rProduct.brandUid !== kNilUuid &&
      rCompanyId !== rProduct.brandUid
    ) {
      kNav.navigate(kRoute.P100, { brand: rCompanyName });
    }
  }, [kNav, rCompanyId, rCompanyName, rCurrentUserType, rProduct.brandUid]);

  return (
    <>
      <ShopSubHeader />
      <ScrollView>
        <View style={rSmallScreen ? styles.P101_ProductContainerCol : styles.P101_ProductContainerRow}>
          <ImageArea />
          <View style={styles.flex1}>
            <View style={styles.P101_InfoArea}>
              <View style={styles.P101_InfoHeader}>
                <View style={styles.row}>
                  <TouchableOpacity onPress={_onPressBrandName}>{BrandNameButton}</TouchableOpacity>
                  <Text style={fonts.H4_BrandDark}>{` > `}</Text>
                  <TouchableOpacity onPress={_onPressPrimaryCategory}>{MainCategoryButton}</TouchableOpacity>
                  <Text style={fonts.H4_BrandDark}>{` > `}</Text>
                  <TouchableOpacity onPress={_onPressSecondaryCategory}>{SubCategoryButton}</TouchableOpacity>
                </View>
              </View>

              <Text style={[fonts.H1_BrandDark, styles.P101_ProductName]}>{rProduct.name}</Text>
              <TouchableOpacity style={styles.P101_BrandNameClickable} onPress={_onPressBrandName}>
                {BrandNameSmallerButton}
              </TouchableOpacity>
              <PriceDisplay />
              <Text
                style={[styles.fPoppinsLightBlack, styles.P99_BrandCardPriceText]}
              >{`CASE PACK: ${rProduct.case_minimum}`}</Text>
              <View style={styles.P101_QuantityArea}>
                <Text style={fonts.H3_BrandDark}>Quantity</Text>
                <QuantityControl />
              </View>
              <View style={styles.P101_VariantsArea}>
                {rProduct.variantOptions.map((value, index) => (
                  <VariantItem key={`${value}`} value={value} index={index} />
                ))}
              </View>
              <Tester />
              <AddToCart />
              <Info title={"Product Info"} content={rProduct.description} openDefault={true} />
              <Info
                title={"Product Dimensions"}
                content={`${rProduct.width} x ${rProduct.height} x ${rProduct.length}`}
              />
              <Info title={"Care Instructions"} content={rProduct.useAndCare ?? ""} />
              <Info title={"Ingredients"} content={rProduct.ingredients ?? ""} />
              <Info title={"Materials"} content={rProduct.materials ?? ""} />
            </View>
          </View>
        </View>
      </ScrollView>
    </>
  );
};
const filterOptions = (
  myVariantOptionName: string,
  myVariantOptionValue: tOption<string>,
  _myVariants: tVariant[],
  _currentValues: { [name: string]: string },
  _numberOfVariants: number,
): boolean => {
  if (_numberOfVariants === 1) {
    const myCurrentVariant = _myVariants.find(
      (aVariant) =>
        aVariant.options.find(
          (myVariantOption) =>
            myVariantOption.name === myVariantOptionName && myVariantOption.label === myVariantOptionValue.myValue,
        ) !== undefined,
    );
    return !!myCurrentVariant?.inventory ?? false;
  }
  return (
    _myVariants.findIndex((_myVar) => {
      return (
        Object.entries(_currentValues).every(
          (val) => val[0] === myVariantOptionName || _myVar.options.find((v) => v.name === val[0])?.label === val[1],
        ) && _myVar.options.find((v) => v.name === myVariantOptionName)?.label === myVariantOptionValue.myValue
      );
    }) !== -1
  );
};

type tpVariantItem = (props: { value: tVariantOption; index: number }) => React.ReactElement;
const VariantItem: tpVariantItem = ({ value: { name: myName, labels: myLabels }, index: myIndex }) => {
  const dispatch = useAppDispatch();
  const rNumOptions = useSelector(
    ({ ProductDetailsReducer }) => ProductDetailsReducer.productData.variantOptions.length,
  );
  const rOptionValue = useSelector(
    ({ ProductDetailsReducer }) => ProductDetailsReducer.variantSelections[myName] ?? undefined,
  );
  const rPurchasableVariants = useSelector(({ ProductDetailsReducer }) =>
    ProductDetailsReducer.productData.variants.filter((variant) => variant.purchasable),
  );
  // const rVariants = useSelector(({ ProductDetailsReducer }) => ProductDetailsReducer.productData.variants);
  const rOptionValues = useSelector(({ ProductDetailsReducer }) => ProductDetailsReducer.variantSelections);
  const _onSelectVariantOption = useCallback(
    (myOption) => {
      dispatch({
        type: "SET_PRODUCT_DETAILS_VARIANT_SELECTION",
        name: myName,
        label: myOption.myValue,
      });
    },
    [dispatch, myName],
  );
  const _onSelectNotVariantOption = useCallback(
    (myOption) => {
      dispatch({
        type: "RESET_PRODUCT_DETAILS_VARIANT_SELECTION",
        name: myName,
        label: myOption.myValue,
      });
    },
    [dispatch, myName],
  );
  const _filterOptions = useCallback(
    (myOption) => {
      return filterOptions(myName, myOption, rPurchasableVariants, rOptionValues, rNumOptions);
    },
    [myName, rNumOptions, rOptionValues, rPurchasableVariants],
  );

  return (
    <View
      style={[
        styles.P101_VariantContainer,
        {
          zIndex: rNumOptions - myIndex,
        },
      ]}
    >
      <Text style={[styles.P101_VariantText, fonts.H3_BrandDark]}>{myName}</Text>
      <ComboBox
        fontStyle={fonts.H3_BrandDark}
        value={rOptionValue}
        myPlaceholder={"Select an option"}
        myOptions={myLabels.map((myLabel) => {
          return {
            myValue: myLabel,
          };
        })}
        onSelect={_onSelectVariantOption}
        onSelectUnavailable={_onSelectNotVariantOption}
        filterAvailable={_filterOptions}
      />
    </View>
  );
};

type tpPriceDisplay = () => React.ReactElement;

const PriceDisplay: tpPriceDisplay = () => {
  const rWsp = useSelector(({ ProductDetailsReducer }) => ProductDetailsReducer.wsp);
  const rMsrp = useSelector(({ ProductDetailsReducer }) => ProductDetailsReducer.msrp);
  return (
    <View style={styles.P101_Pricing}>
      <Text style={fonts.H4_Selected_BrandDark}>{`WSP ${formatMoney(rWsp)}`}</Text>
      <Text style={fonts.H4_BrandDark}>{`  |  MSRP ${formatMoney(rMsrp)}`}</Text>
    </View>
  );
};

type tpAddToCart = () => React.ReactElement;

const AddToCart: tpAddToCart = () => {
  const dispatch = useAppDispatch();
  const rInventory = useSelector(({ ProductDetailsReducer }) => ProductDetailsReducer.inventory);
  const rIsAvailable = useSelector(({ ProductDetailsReducer }) => ProductDetailsReducer.isAvailable);
  const rIsLoading = useSelector(({ LoadingReducer }) => LoadingReducer.addingProduct);
  const rProductDetails = useSelector((state) => state.ProductDetailsReducer);

  const _onPress = useCallback(() => {
    dispatch(addProductToCart(rProductDetails));
  }, [dispatch, rProductDetails]);

  const aDisabled = !rIsAvailable || !rInventory || rIsLoading;

  return (
    <TouchableOpacity
      disabled={aDisabled}
      style={[styles.P101_AddToCartButton, aDisabled ? styles.buttonDisabled : {}]}
      onPress={_onPress}
    >
      {rIsLoading ? (
        <ActivityIndicator color={kColors.white} />
      ) : (
        <Text style={fonts.ButtonText_White}>{!rInventory ? "OUT OF STOCK" : "ADD TO CART"}</Text>
      )}
    </TouchableOpacity>
  );
};

type tpTester = () => React.ReactElement;

const Tester: tpTester = () => {
  const dispatch = useAppDispatch();
  const rShowTester = useSelector(({ ProductDetailsReducer }) => ProductDetailsReducer.showTester);
  const rTesterPrice = useSelector(({ ProductDetailsReducer }) => ProductDetailsReducer.testerPrice);
  const rTesterSelected = useSelector(({ ProductDetailsReducer }) => ProductDetailsReducer.testerSelected);

  const _onPressCheckbox = useCallback(() => {
    dispatch({
      type: "SET_PRODUCT_DETAILS_TESTER",
      value: (value) => !value,
    });
  }, [dispatch]);

  return (
    <>
      {rShowTester && (
        <CheckMark2
          style={styles.P101_TesterArea}
          textStyle={styles.P101_TesterText}
          myText={`Include a Tester for ${formatMoney(rTesterPrice)}`}
          myState={rTesterSelected}
          textPlacement="right"
          onPress={_onPressCheckbox}
        />
      )}
    </>
  );
};

type tpInfo = (props: { title: string; content: string; openDefault?: boolean }) => React.ReactElement;

const Info: tpInfo = ({ title, content, openDefault }) => {
  const [sOpen, setOpen] = useState<boolean>(openDefault ?? false);

  const _onPress = useCallback(() => {
    setOpen((value) => !value);
  }, []);

  const TitleButton = useMemo(() => <Text style={fonts.H4_Selected_BrandDark}>{title}</Text>, [title]);

  return (
    <>
      <View style={styles.P101_Divider} />
      <TouchableOpacity onPress={_onPress}>{TitleButton}</TouchableOpacity>
      {sOpen && <Text style={[fonts.H4_BrandDark, styles.P101_DescriptionText]}>{content}</Text>}
    </>
  );
};

type tpImageListItem = (props: { myImageUrl: string }) => React.ReactElement;
const ImageListItem: tpImageListItem = ({ myImageUrl }) => {
  const dispatch = useAppDispatch();
  const rDisplayImage = useSelector(({ ProductDetailsReducer }) => ProductDetailsReducer.displayImage);

  const _onPress = useCallback(() => {
    dispatch({
      type: "SET_PRODUCT_DETAILS_DISPLAY_IMAGE",
      value: myImageUrl,
    });
  }, [dispatch, myImageUrl]);

  const aStyle = rDisplayImage === myImageUrl ? styles.P101_ThumbnailSelected : styles.P101_Thumbnail;
  const Button = useMemo(
    () => (
      <View style={aStyle}>
        <SLImage
          source={{ uri: myImageUrl }}
          mobileStyle={styles.P101_ProductImageThumbnailButton}
          webStyle={webStyles.P101_ImageThumbnailButton}
        />
      </View>
    ),
    [aStyle, myImageUrl],
  );

  return (
    <TouchableOpacity key={myImageUrl} style={styles.P101_ImageThumbnailButton} onPress={_onPress}>
      {Button}
    </TouchableOpacity>
  );
};

type tpImageArea = () => React.ReactElement;

const ImageArea: tpImageArea = () => {
  const dispatch = useAppDispatch();
  const rProductImages = useSelector(({ ProductDetailsReducer }) => ProductDetailsReducer.productImages);
  const rDisplayImage = useSelector(({ ProductDetailsReducer }) => ProductDetailsReducer.displayImage);
  const rSmallScreen = useSelector(({ NavigationReducer }) => NavigationReducer.drawerType === "slide");

  const _onPressDecrement = useCallback(() => {
    dispatch({
      type: "SET_PRODUCT_DETAILS_DISPLAY_IMAGE",
      value: (myPrevIndex) => myPrevIndex - 1,
    });
  }, [dispatch]);

  const _onPressIncrement = useCallback(() => {
    dispatch({
      type: "SET_PRODUCT_DETAILS_DISPLAY_IMAGE",
      value: (myPrevIndex) => myPrevIndex + 1,
    });
  }, [dispatch]);

  return (
    <View style={rSmallScreen ? styles.P101_ImageAreaSmall : styles.P101_ImageArea}>
      {rSmallScreen || (
        <View style={styles.P101_ImageList}>
          {rProductImages.map((myImageUrl) => (
            <ImageListItem key={myImageUrl} myImageUrl={myImageUrl} />
          ))}
        </View>
      )}
      <View style={styles.flex1}>
        <SLImage
          source={{ uri: rDisplayImage }}
          mobileStyle={rSmallScreen ? styles.P101_ImageSmall : styles.P101_Image}
          webStyle={rSmallScreen ? webStyles.P101_ImageSmall : webStyles.P101_Image}
        />
        {rSmallScreen && (
          <View style={styles.P101_DotImageList}>
            {rProductImages.map((myImageUrl) => (
              <View style={rDisplayImage === myImageUrl ? styles.P101_DotSelected : styles.P101_Dot} />
            ))}
          </View>
        )}
        {rSmallScreen && (
          <View style={styles.P101_ImageClickableArea}>
            <TouchableOpacity style={styles.P101_ImageClickableLeft} onPress={_onPressDecrement}>
              <View style={styles.carouselButton}>
                <PdArrowLeft color={kColors.brandDark} style={styles.carouselIcon} />
              </View>
            </TouchableOpacity>
            <TouchableOpacity style={styles.P101_ImageClickableRight} onPress={_onPressIncrement}>
              <View style={styles.carouselButton}>
                <PdArrowRight color={kColors.brandDark} style={styles.carouselIcon} />
              </View>
            </TouchableOpacity>
          </View>
        )}
      </View>
    </View>
  );
};

type tpQuantityControl = () => React.ReactElement;

const QuantityControl: tpQuantityControl = () => {
  const dispatch = useAppDispatch();
  const rRawQuantity = useSelector(({ ProductDetailsReducer }) => ProductDetailsReducer.rawQuantity);
  const [sBigPlus, setBigPlus] = useState<boolean>(false);
  const [sBigMinus, setBigMinus] = useState<boolean>(false);

  const handleOnPressSubtractQuantity = () => {
    setBigMinus(true);
    dispatch({ type: "PRODUCT_DETAILS_DECREMENT_QUANTITY" });
  };

  const handleOnPressAddQuantity = () => {
    setBigPlus(true);
    dispatch({ type: "PRODUCT_DETAILS_ADD_QUANTITY" });
  };

  const handleOnChangeQuantity = (inputQuantity: number | null) => {
    dispatch({ type: "PRODUCT_DETAILS_CHANGE_QUANTITY", payload: inputQuantity || undefined });
  };

  const handleOnBlur = () => {
    dispatch({ type: "SET_PRODUCT_DETAILS_BOUND_QUANTITY" });
  };

  const _onAnimationEndMinus = useCallback(() => {
    setBigMinus(false);
  }, []);

  const _onAnimationEndPlus = useCallback(() => {
    setBigPlus(false);
  }, []);

  const PlusButton = useMemo(() => {
    return <AnimatedPlusSign size={sBigPlus ? "big" : "small"} onAnimationComplete={_onAnimationEndPlus} />;
  }, [_onAnimationEndPlus, sBigPlus]);

  const MinusButton = useMemo(() => {
    return <AnimatedMinusSign size={sBigMinus ? "big" : "small"} onAnimationComplete={_onAnimationEndMinus} />;
  }, [_onAnimationEndMinus, sBigMinus]);

  return (
    <View style={styles.P101_QuantityControl}>
      <TouchableOpacity onPress={handleOnPressSubtractQuantity}>{MinusButton}</TouchableOpacity>
      <PdInputNumber
        style={[styles.P101_QuantityInput, fonts.H3_BrandDark, styles.P101_QuantityInputText]}
        value={rRawQuantity}
        maxLength={4}
        onChangeNumber={handleOnChangeQuantity}
        onBlur={handleOnBlur}
      />
      <TouchableOpacity onPress={handleOnPressAddQuantity}>{PlusButton}</TouchableOpacity>
    </View>
  );
};

export const P101 = React.memo(P101_ProductDetails);

const webStyles: { [key: string]: CSSProperties } = {
  P101_ImageThumbnailButton: {
    height: 55,
    width: 55,
    objectFit: "cover",
    backgroundColor: kColors.white,
  },
  P101_Image: {
    objectFit: "contain",
    objectPosition: "top",
    flex: 1,
    height: 761,
    backgroundColor: kColors.white,
  },
  P101_ImageSmall: {
    objectFit: "contain",
    height: 380,
    backgroundColor: kColors.white,
  },
} as const;

const styles = StyleSheet.create({
  P101_AddToCartButton: {
    ...kStyleConst.kAddToCartButtonDimensions,
    alignItems: "center",
    backgroundColor: kColors.brandDarkGreen,
    justifyContent: "center",
    marginBottom: 4 * kStyleConst.kSpacing,
    marginTop: 8 * kStyleConst.kSpacing,
  },
  P101_BrandNameClickable: {
    alignSelf: "flex-start",
  },
  P101_DescriptionText: {
    marginRight: 4 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  P101_Divider: {
    borderBottomColor: kColors.brandDark,
    borderBottomWidth: kStyleConst.kBorderWidthSmall,
    marginBottom: 5 * kStyleConst.kSpacing,
    paddingBottom: 8 * kStyleConst.kSpacing,
  },
  P101_Dot: {
    ...kStyleConst.kDotDimensions,
    backgroundColor: kColors.brandDarkTransparent,
    borderRadius: 999,
    marginHorizontal: kStyleConst.kSpacing,
  },
  P101_DotImageList: {
    bottom: 4 * kStyleConst.kSpacing,
    flexDirection: "row",
    position: "absolute",
    right: 3 * kStyleConst.kSpacing,
  },
  P101_DotSelected: {
    ...kStyleConst.kDotDimensions,
    backgroundColor: kColors.brandDark,
    borderRadius: 999,
    marginHorizontal: kStyleConst.kSpacing,
  },
  P101_Image: {
    backgroundColor: kColors.white,
    flex: 1,
    height: 761,
    objectFit: "contain",
    objectPosition: "top",
  },
  P101_ImageArea: {
    alignItems: "flex-start",
    alignSelf: "flex-start",
    flex: 1,
    flexDirection: "row",
    marginTop: 12.5 * kStyleConst.kSpacing,
  },
  P101_ImageAreaSmall: {
    alignItems: "center",
    alignSelf: "stretch",
    flexDirection: "row",
  },
  P101_ImageClickableArea: {
    bottom: 0,
    flexDirection: "row",
    left: 0,
    position: "absolute",
    right: 0,
    top: 0,
  },
  P101_ImageClickableLeft: {
    alignItems: "center",
    bottom: 0,
    justifyContent: "center",
    left: 0,
    paddingHorizontal: 2 * kStyleConst.kSpacing,
    position: "absolute",
    top: 0,
  },
  P101_ImageClickableRight: {
    alignItems: "center",
    bottom: 0,
    justifyContent: "center",
    paddingHorizontal: 2 * kStyleConst.kSpacing,
    position: "absolute",
    right: 0,
    top: 0,
  },
  P101_ImageList: {
    alignSelf: "flex-start",
    justifyContent: "center",
    marginHorizontal: 6 * kStyleConst.kSpacing,
    marginTop: 0,
  },
  P101_ImageSmall: {
    backgroundColor: kColors.white,
    height: 380,
    objectFit: "contain",
  },
  P101_ImageThumbnailButton: {
    marginBottom: 2 * kStyleConst.kSpacing,
  },
  P101_InfoArea: {
    flex: 1,
    padding: 8 * kStyleConst.kSpacing,
  },
  P101_InfoHeader: {
    alignItems: "flex-start",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  P101_Pricing: {
    alignItems: "center",
    flexDirection: "row",
    marginBottom: kStyleConst.kSpacing,
    marginTop: 8 * kStyleConst.kSpacing,
  },
  P101_ProductContainerCol: {
    alignItems: "stretch",
    flexDirection: "column",
  },
  P101_ProductContainerRow: {
    flexDirection: "row",
    marginBottom: 60 * kStyleConst.kSpacing,
  },
  P101_ProductImageThumbnailButton: {
    backgroundColor: kColors.white,
    height: 55,
    objectFit: "cover",
    width: 55,
  },
  P101_ProductName: {
    marginBottom: 2 * kStyleConst.kSpacing,
    marginTop: 8 * kStyleConst.kSpacing,
    overflowWrap: "normal",
  },
  P101_QuantityArea: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "flex-start",
    marginVertical: 5 * kStyleConst.kSpacing,
  },
  P101_QuantityControl: {
    flexDirection: "row",
    marginLeft: 5 * kStyleConst.kSpacing,
  },
  P101_QuantityInput: {
    ...kStyleConst.kQuantityInputDimensions,
    borderColor: kColors.brandDark,
    borderWidth: kStyleConst.kBorderWidth,
    marginHorizontal: 4 * kStyleConst.kSpacing,
  },
  P101_QuantityInputText: {
    textAlign: "center",
  },
  P101_TesterArea: {
    alignItems: "center",
    alignSelf: "flex-start",
    flexDirection: "row",
    marginTop: 8 * kStyleConst.kSpacing,
  },
  P101_TesterText: {
    color: kColors.brandDark,
    fontFamily: "Poppins",
    fontSize: 16,
    fontWeight: "500",
    justifyContent: "flex-start",
    marginLeft: 2 * kStyleConst.kSpacing,
  },
  P101_Thumbnail: {
    ...kStyleConst.kThumbnailDimensions,
    opacity: kStyleConst.kImageThumbnailOpacity,
  },
  P101_ThumbnailSelected: {
    ...kStyleConst.kThumbnailDimensions,
  },
  P101_VariantContainer: {
    alignItems: "flex-end",
    flexDirection: "row",
    marginVertical: kStyleConst.kSpacing,
  },
  P101_VariantText: {
    marginRight: 8 * kStyleConst.kSpacing,
    minWidth: 11 * kStyleConst.kSpacing,
  },
  P101_VariantsArea: {
    zIndex: 10,
  },
  P99_BrandCardPriceText: {
    marginBottom: kStyleConst.kSpacing,
  },
  buttonDisabled: {
    opacity: 0.4,
  },
  carouselButton: {
    alignItems: "center",
    height: 30,
    justifyContent: "center",
    padding: 5,
    width: 30,
  },
  carouselIcon: {
    width: 22,
  },
  fPoppinsLightBlack: {
    ...kFonts.poppinsLight,
    color: kColors.black,
  },
  flex1: { flex: 1 },
  row: {
    flexDirection: "row",
    marginTop: 3 * kStyleConst.kSpacing,
    position: "relative",
    zIndex: 1000,
  },
});
